/**
 * ONE DEVICE, MERGED THE SAME WAY THE FLEET TABLE MERGES ALL OF THEM.
 *
 * 🔴 THIS HOOK DELIBERATELY DOES NOT HAVE ITS OWN IDEA OF WHAT A DEVICE IS.
 *
 * A fleet row is not a record — it is the JOIN of three reads: the device's
 * check-in, the hardware unit it was installed onto, and the release target the
 * server resolved for it. `buildFleetRows` already does that join, including
 * the three-key unit match and the "never claim on-target without evidence"
 * rule. Re-deriving any of it here would give the device page a subtly
 * different answer from the table that linked to it, about the same device, on
 * the same screen — which is the failure the merged row exists to end.
 *
 * So this hook does exactly what `BrandingTab` does, for one id, and then picks
 * the row out. It imports `loadInstalledUnits` from that file rather than
 * copying its two gates (the INSTALLED_STATES filter and `!!u.osInstall`),
 * because a copy of those is how the two views would begin to disagree about
 * whether a device is in the fleet at all.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { api } from '../../../services/apiService';
import type { DeviceModelRow } from '../../../services/hardwareApi';
import {
  buildFleetRows, resolvedTargetMapFromEntries,
  type FleetRow, type FleetRegisterLinkMap,
} from '../fleet/fleetRows';
import { loadInstalledUnits } from '../branding/BrandingTab';

/** The fleet's only device type today; `?type=` overrides it for the future. */
export const DEFAULT_DEVICE_TYPE = 'android_dual_screen';

export interface FleetDeviceState {
  row: FleetRow | null;
  model: DeviceModelRow | null;
  /** True only until the FIRST load settles — a refresh must not blank the page. */
  loading: boolean;
  /** Set when the check-in read failed. Distinct from "no such device". */
  error: string | null;
  /**
   * The check-in read succeeded and this device was not in it.
   *
   * Kept apart from `error` on purpose: "we could not look" and "we looked and
   * it is not there" want different words on screen, and collapsing them is how
   * a network blip starts reading as a deleted till.
   */
  notFound: boolean;
  refresh: () => void;
}

const POLL_MS = 15000;

export function useFleetDevice(
  deviceId: string | undefined,
  deviceType: string = DEFAULT_DEVICE_TYPE,
): FleetDeviceState {
  const [row, setRow] = useState<FleetRow | null>(null);
  const [model, setModel] = useState<DeviceModelRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const live = useRef(true);

  const load = useCallback(async () => {
    if (!deviceId) return;
    try {
      const [status, targets, unitsAndModels, links] = await Promise.all([
        // Shaped as one object rather than a discriminated union: `Promise.all`
        // widens the tuple enough that TypeScript stops narrowing on `ok`, and
        // a cast to get the narrowing back would be a cast over the one value
        // that says whether this read can be trusted.
        deviceConfigApi.listDeviceStatus(deviceType).then(
          (s) => ({ devices: (s?.devices || []) as any[], readError: null as string | null }),
          (e: any) => ({ devices: [] as any[], readError: (e?.message || 'Could not load this device') as string | null }),
        ),
        // A target that cannot be read must never take the page down: the
        // identity, health and support tabs do not depend on it, and the
        // release card says "unknown" rather than guessing.
        deviceConfigApi.listResolvedTargets(deviceType).catch(() => null),
        loadInstalledUnits().catch(() => ({ units: [], models: [] as DeviceModelRow[] })),
        // One id, the same endpoint the table calls with all of them.
        api.posConfig.getRegisterLinks([deviceId])
          .then((r) => Object.fromEntries(r.links.map((l: any) => [l.registerId, l])) as FleetRegisterLinkMap)
          .catch(() => ({} as FleetRegisterLinkMap)),
      ]);
      if (!live.current) return;

      if (status.readError) {
        // A failed read leaves whatever we already had on screen. Blanking the
        // page on a blip loses the operator's place mid-support-call.
        setError(status.readError);
        setLoading(false);
        return;
      }

      const rows = buildFleetRows(
        status.devices,
        unitsAndModels.units,
        targets ? resolvedTargetMapFromEntries(targets.targets, {}, targets.deviceType || deviceType) : {},
        { deviceType },
        links,
      );
      const found = rows.find((r) => r.deviceId === deviceId) || null;
      setRow(found);
      setNotFound(!found);
      setError(null);
      setModel(
        found?.modelId
          ? unitsAndModels.models.find((m) => m.modelId === found.modelId) || null
          : null,
      );
    } catch (e: any) {
      if (!live.current) return;
      setError(e?.message || 'Could not load this device');
    } finally {
      if (live.current) setLoading(false);
    }
  }, [deviceId, deviceType]);

  useEffect(() => {
    live.current = true;
    void load();
    // Same cadence as the fleet table, so a device open in both does not show
    // two different "last seen" values a few seconds apart.
    const t = setInterval(() => { void load(); }, POLL_MS);
    return () => { live.current = false; clearInterval(t); };
  }, [load]);

  return { row, model, loading, error, notFound, refresh: load };
}
